import { EntityTypeIds } from 'src/app/constants.new';
import { ReportType } from './report-type';
import { stubTrue } from 'lodash';

export class ReturnAssetDetailIncludeAssetDescriptorModel {
    descriptorId: number;
    include: boolean;
    name: string;
}

export class ReportDetailCriteria {
    reportType: ReportType;
    entitySelection: EntitySelection;
    frequentlyUsed: boolean;
    hideTopLevelCompanyPicker: boolean;
    showSupportInstancePicker: boolean;

    // Further Limit Panel
    // PSR
    taxYearBegin: number;
    taxYearEnd: number;
    includeEstimatedAssessments: boolean;
    includeOnlySitesUnderAppeal: boolean;
    //Attachment Bundler
    includeBlankTaxYear: boolean;
    attachmentLocations: Core.AttachmentBundlerLocationEnum[];
    attachmentTypes: Core.AttachmentTypes[];
    attachmentCategories: Core.AttachmentCategories[];
    ignoreConditionalFilterOptions: boolean;
    // Budget
    budgetYear: number;
    budgetYearString: string; // calculate field for jsreport
    minimumDetailYearsShown: number;
    includePPPropertyType: boolean;
    //Budget Variance
    budgetPeriods: BudgetPeriodSelection[];
    // MAAR
    propertyTypeID: number;
    taxYear: number;
    //Project Status
    siteStatus: number;
    propertyTypeIDs: number[];
    //Site
    numberOfYearsToInclude: number;
    yearChoice: string;
    //Return Schedule Summary
    //Return Asset Detail
    formRevisionId: number;
    scheduleId: number;
    scheduleName?: string;
    //Return Asset Detail
    priorReturnStatuses: Compliance.ReturnAssetPriorReturnStatusEnum[];
    priorReturnStatusName?: string;
    //User Activity
    loginDatePeriod: number;
    loginDateBegin: Date;
    loginDateEnd: Date;
    onlyActiveUsers: boolean;
    includeUsersWithoutLogin: boolean;
    // Value
    showExemptions: boolean;
    showAllParcels: boolean;
    filingDeadlineStart: Date;
    filingDeadlineEnd: Date;
    nodBatchNumber: string;
    isPreliminary: boolean;

    // Format Output Panel
    // PSR
    psrFormatOutputOptions: PsrReportCriteria;

    // Budget
    summaryReportOptions: SummaryReportCriteria;
    detailReportOptions: ExtraReportDetailCriteria;
    currCompanyBudgetID: number;
    priorCompanyBudgetID: number;
    budgetBasis: Core.CompanyBudgetBasisEnum;
    // Budget Variance
    bvrFormatOutput: BudgetVarianceFormatOutputCriteria;
    // Site
    orderBy: number[];
    siteFormatOutputOptions: SiteReportCriteria;
    //Return Schedule Summary
    title: string;
    showTaxpayerName: boolean;
    splitOutFactors: boolean;
    suppressZeroCost: boolean;
    showGrandTotal: boolean;
    includePriorCostAddsDeletes: boolean;
    includeFactorAndDepreciatedValue: boolean;
    includeRatioAndAssessedValue: boolean;
    //Return Asset Detail
    groupBy: string[];
    showChangeDetails: boolean;
    showTransferDetails: boolean;
    showLandscape: boolean;
    includeAcqYear: boolean;
    includeGLAccount: boolean;
    includeScheduleName: boolean;
    includeFactorTableName: boolean;
    includeAssetDescriptors: ReturnAssetDetailIncludeAssetDescriptorModel[];
    watermark: string;
    includeAdditionalDepreciation: boolean;
    includeEstimatedFMV: boolean;
    includeCost: boolean;
    includeDisposedDate: boolean;
    includeCostChange: boolean;
    scheduleFilterType: Compliance.ReportScheduleFilterTypeEnum;
    //User Activity
    userActivityFormatOutputOptions: UserActivityReportCriteria;

    //Lease
    includeLeaseClientId: boolean;
    includeLeaseLocationName: boolean;
    includeLeaseLocationAddress: boolean;
    includeLeaseStartDate: boolean;
    includeLeaseEndDate: boolean;
    includeLeaseTerm: boolean;
    includeLeaseMonthlyPayments: boolean;
    includeLeaseType: boolean;
    includeLeaseAssestId: boolean;
    includeLeaseNumber: boolean;
    includeLeaseBillingInfo: boolean;
    includeLinkedSubParcelAcctNumber: boolean;
    includeLinkedSubParcelDescription: boolean;
    includeLinkedSubAssessorAbbr: boolean;
    groupByLinkedSubParcelAcctNum: boolean;
    groupByLinkedSubAssessorAbbr: boolean;
    groupByLinkedSubParcelDescription: boolean;

    //User Access
    selectedContact: Core.ContactModel;
    startWith: number;
    limitByLoginDate: number;
    includeInternalUsers;

    //Maar
    includeAllCompanies;

    // All
    outputFormat: number;
    includeAssetClass: boolean;
    assetType: number;
    groupByAssetClass: boolean;
    legalSize: boolean;
    includeDisclaimer: boolean;

    dataSource: string;

    // Support Instance Escalation Activity
    limitByEscalationDate: number;
    escalationDatePeriod: number;
    escalationDateBegin?: Date;
    escalationDateEnd?: Date;
    anyEscalationDates: boolean;
    escalationGroupBy: Core.SupportInstanceEscalationActivityReportGroupByEnum;

    // Value
    paperSize: number;
    valueOrderBy: number;
}

// I'm not crazy about this name; I think we should consider renaming ReportDetailCriteria
// to something like "ReportCriteria", since it's really the owner object for all other criteria,
// then we could name this DetailReportCriteria to match the name of the panel (if we named it
// that now we'd have a ReportDetailCriteria and DetailReportCritera class, and I'm not doing that).
export class ExtraReportDetailCriteria {
    constructor() {
        this.showLienDate = true;
        this.valueDetails = 1;
        this.includeAssessedValueInfo = 1;
        this.includeTargetValue = 2;
        this.includeValuationMetrics = 1;
        this.breakOutTaxPaymentDetails = 1;
        this.showPercentChange = 0;
        this.includePaymentDueDates = false;
        this.includeBudgetComments = true;
        this.includeAlternateAssessments = true;
    }

    showLienDate: boolean;
    valueDetails: number;
    includeAssessedValueInfo: number;
    includeTargetValue: number;
    includeValuationMetrics: number;
    breakOutTaxPaymentDetails: number;
    showPercentChange: number;
    includePaymentDueDates: boolean;
    includeBudgetComments: boolean;
    includeAlternateAssessments: boolean;
}

export class SiteReportCriteria {
    constructor() {
        this.includeParcelAssessmentGrid = false;
        this.includeCompanyNameDetails = 0;
        this.includeFmvChart = true;
        this.includeSiteAppealComments = false;
        this.includePinnedParcelComment = false;
    }

    includeParcelAssessmentGrid: boolean;
    includeCompanyNameDetails: number;
    includeFmvChart: boolean;
    includeSiteAppealComments: boolean;
    includePinnedParcelComment: boolean;
}

export class UserActivityReportCriteria {
    constructor() {
        this.showUserName = false;
        this.showContactTitle = false;
        this.showLoginDates = true;
        this.showLoginDayCount = true;
        this.showLastPasswordChange = false;
    }

    showUserName: boolean;
    showContactTitle: boolean;
    showLoginDates: boolean;
    showLoginDayCount: boolean;
    showLastPasswordChange: boolean;
}

export class PsrReportCriteria {
    constructor(reportTypeId: number) {
        if (reportTypeId == ReportType.AppealRecommendation) {
            this.paperSize = 1;
            this.showAcqInfo = true;
            this.showYearBuiltInfo = false;
            this.showAppealDeadlines = true;
            this.showAssessments = 0;
            this.showMetrics = 1;
            this.showInitValue = 2;
            this.showTargetValue = 0;
            this.showLatestValue = 1;
            this.showTaxDetails = 2;
            this.showAppealDetails = true;
            this.showComments = false;
            this.showParcelList = false;
            this.showSiteAppealCommentsValue = 2;
            this.showSavings = 4;
            this.orderGroupBy = [];
            this.showTaxableValues = true;
            this.showExemptions = true;
            this.showEmptyColumns = false;
        } else {
            this.paperSize = 1;
            this.showAcqInfo = false;
            this.showYearBuiltInfo = false;
            this.showAppealDeadlines = false;
            this.showAssessments = 0;
            this.showMetrics = 1;
            this.showInitValue = 2;
            this.showTargetValue = 0;
            this.showLatestValue = 1;
            this.showTaxDetails = 2;
            this.showAppealDetails = true;
            this.showComments = true;
            this.showParcelList = false;
            this.showSiteAppealCommentsValue = 0;
            this.showSavings = 4;
            this.orderGroupBy = [];
            this.showTaxableValues = true;
            this.showExemptions = true;
            this.showEmptyColumns = false;
        }
    }

    paperSize: number;
    showAcqInfo: boolean;
    showYearBuiltInfo: boolean;
    showAppealDeadlines: boolean;
    showAssessments: number;
    showMetrics: number;
    showInitValue: number;
    showTargetValue: number;
    showLatestValue: number;
    showTaxDetails: number;
    showAppealDetails: boolean;
    showComments: boolean;
    showParcelList: boolean;
    showSiteAppealCommentsValue: number;
    showSavings: number;
    orderGroupBy: OrderAndTotalByOption[];
    showParcelsAddedSince: Date;
    showParcelsAddedSinceCheckbox: boolean;
    showTaxableValues: boolean;
    showExemptions: boolean;
    showEmptyColumns: boolean;
}

export class BaseFormatOutputOptions {
    constructor() {
        this.outputFormatOptions = ['PDF', 'Excel'];
        this.paperSizeOptions = ['Letter', 'Legal', 'A4', 'A3', 'A5', 'Tabloid'];
    }

    outputFormatOptions: string[];
    paperSizeOptions: string[];
}

export interface ValueOrderByOption {
    id: number;
    name: string;
    primarySortOrder: number;
}

export class FormatOutputOptionsValue extends BaseFormatOutputOptions {
    constructor() {
        super();
        this.valueOrderByOptions = [];
    }

    valueOrderByOptions: ValueOrderByOption[];
}

export class FormatOutputOptionsPsr extends BaseFormatOutputOptions {
    constructor() {
        super();
        this.showAssessmentsOptions = true
            ? ['MV', 'Alternate', 'MV and Alternate']
            : ['FMV', 'Alternate', 'FMV and Alternate'];
        this.showMetricsOptions = ['None', 'Most applicable based on site class', 'All present'];
        this.showValueOptions = ['No', 'Yes (Incl. Metrics)', 'Yes (No Metrics)'];
        this.showTaxDetailsOptions = [
            'None',
            'Latest Taxes (Incl. Metrics)',
            'Latest Taxes (No Metrics)',
            'All Taxes (Incl. Metrics)',
            'All Taxes (No Metrics)',
        ];
        this.showSiteAppealCommentsOptions = ['No', 'Latest Year', 'All Years'];
        this.showLatestValueOptions = [
            'Yes (Incl. Metrics and YOY % Change)',
            'Yes (Incl. Metrics)',
            'Yes (Incl. YOY % Change )',
            'Yes (No Metrics or YOY % Change)',
        ];
        this.showSavingsOptions = [
            { name: 'Received and Projected (Combined)', value: Core.PsrShowSavings.ReceivedAndProjectedCombined },
            { name: 'Received and Projected', value: Core.PsrShowSavings.ReceivedAndProjected },
            { name: 'Received Only', value: Core.PsrShowSavings.ReceivedOnly },
            { name: 'Projected Only', value: Core.PsrShowSavings.ProjectedOnly },
            { name: 'No', value: Core.PsrShowSavings.No },
        ];
    }

    showAssessmentsOptions: string[];
    showMetricsOptions: string[];
    showValueOptions: string[];
    showTaxDetailsOptions: string[];
    orderAndTotalByOptions: OrderAndTotalByOption[];
    showSiteAppealCommentsOptions: string[];
    showLatestValueOptions: string[];
    showSavingsOptions: Compliance.NameValuePair<Core.PsrShowSavings>[];
}

export class OrderAndTotalByOption {
    id: number;
    name: string;
    primarySortOrder: number;
    groupByThisItem?: boolean;
}

export class FormatOutputOptionsBudget {
    constructor() {
        this.outputFormatOptions = [
            'Summary Report (PDF)',
            'Summary Report (Excel)',
            'Summary + Details Report (PDF)',
            'Summary (Excel) + Details (PDF)',
            'Details Report (PDF)',
        ];
        this.summaryCashBasisPeriodOptions = ['Annual', 'Quarterly', 'Monthly', 'None'];
        this.showPercentChangeOptions = ['Yes with Details', 'Yes, no Details', 'No'];
        this.detailLevelOptions = ['Site', 'Parcel', 'None'];
    }

    outputFormatOptions: string[];
    summaryCashBasisPeriodOptions: string[];
    showPercentChangeOptions: string[];
    detailLevelOptions: string[];
}

export class SummaryReportCriteria {
    constructor() {
        this.summaryCashBasisPeriod = 0;
        this.detailLevel = 0;
        this.includeBudgetComments = false; // needs to be flipped to true when comments are added back in
        this.showPercentChange = 0;
        this.orderAndTotalBy = [];
    }

    summaryCashBasisPeriod: number;
    detailLevel: number;
    includeBudgetComments: boolean;
    showPercentChange: number;
    orderAndTotalBy: number[];
}

export class BudgetVarianceFormatOutputCriteria {
    constructor() {
        this.outputFormat = 0;
        this.budgetBasis = Core.CompanyBudgetBasisEnum.AccrualAndCash;
        this.budgetSources = [];
        this.summaryReportOptions = new BudgetVarianceSummaryReportCriteria();
    }

    outputFormat: number; // 0=Summary Report (PDF), 1=Summary Report (Excel)
    budgetBasis: Core.CompanyBudgetBasisEnum;
    budgetSources: BudgetVarianceBudgetSource[]; // 1st period >= 2nd period [ >= 3rd period [ >= 4th period ] ] (newest-to-oldest)
    summaryReportOptions: BudgetVarianceSummaryReportCriteria;
}

export class BudgetVarianceSummaryReportCriteria {
    constructor() {
        this.detailLevel = 0;
        this.varianceDetail = Core.BudgetVarianceDetailEnum.AmountAndPercent;
        this.orderAndTotalBy = [];
    }

    detailLevel: number; // 0=Site, 1=Parcel, 2=None
    varianceDetail: Core.BudgetVarianceDetailEnum;
    orderAndTotalBy: number[];
}

export class BudgetVarianceBudgetSource {
    constructor() {
        this.budgetId = -1;
        this.budgetBasis = Core.CompanyBudgetBasisEnum.AccrualAndCash;
        this.budgetPeriod = {
            periodBegin: new Date(),
            periodEnd: new Date(),
        };
    }

    budgetId: number; // 1st: 0 = Current Forecast  OR  > 0,
    // 2nd: > 0
    // 3rd: -1 = None OR > 0
    // 4th: -1 = None or > 0; Must be -1 if 3rd = -1
    budgetName: string;
    budgetBasis: Core.CompanyBudgetBasisEnum;
    budgetPeriod: Core.TimePeriod;
}

export class BudgetPeriodSelection implements Core.TimePeriod {
    constructor() {
        this.displayName = '';
        this.periodBegin = new Date();
        this.periodEnd = new Date();
    }

    periodBegin: Date;
    periodEnd: Date;

    displayName: string;
}

export class EntitySelection {
    constructor() {
        this.excludeInactive = true;
        this.selectedTopLevelCompanyIDs = [];
        this.selectedEntities = [];
        this.selectedSupportInstanceIds = [];
        this.showValues = EntityTypeIds.PARCEL;
        this.topLevelCompaniesCount = 0;
        this.beginWithStateSource = false;
    }

    excludeInactive: boolean;
    selectedTopLevelCompanyIDs: number[];
    selectedEntities: SelectedEntity[];
    selectedSupportInstanceIds: number[];
    showValues: number;
    topLevelCompaniesCount: number;
    beginWithStateSource: boolean;
}

export class ReportCategory {
    categoryId: number;
    categoryName: string;
    categoryType: number;
    reportList: ReportSummary[];
}

export class ReportSummary {
    reportID: number;
    reportName: string;
    sequence: number;
    variantName: string;
    displayName: string;
}

export class ReportDetail extends ReportSummary {
    categoryID: number;
    categoryName: string;
    criteria: ReportDetailCriteria;
    isOwnedByUser: boolean;
    isShared: boolean;
    isFavorite: boolean;
    reportTypeId: number;
    rowVersion: any;
    isDisabled: boolean;
    isSystemReport: boolean;

    dataSource: string;
}

export class TopLevelCompany {
    companyName: string;
    companyID: number;
    isFavorite: boolean;
}

export class GenericEntity {
    id: number;
    name: string;
}

export interface SelectedEntity {
    name: string;
    buttonName: string;
    //the id is the entity type id. it's saved to the SavedSearch table's Filter column JSON as 'id'.
    id: number;
    prerequisites: number[];
    blockers: number[];
    list: GenericEntity[];
    //the selected ids are entity ids.
    selectedIDs: number[];
    selectedPropCharVals: string[];
    dropdownList: string[];
    selectedDropdown: string;
    loading: boolean;
    isAppealResponsibility?: boolean;
}

export class EntityFilter {
    entityTypeID: number;
    entityIDs: number[];
}

export class GetEntitiesRequest {
    sourceEntityIDs: number[];
    userIdFilters: string[];
    sourceEntityTypeID: number;
    sourceEntityIsTopLevelCompany: boolean;
    sitePropCharFilters: object[];
    parcelPropCharFilters: object[];
    entityFilters: EntityFilter[];
    targetEntityTypeID: number;
    excludeInactive: boolean;
    contactIds: number[];
}

export class GetFurtherLimitRequest {
    startTaxYear?: number;
    endTaxYear?: number;
    includeBlankTaxYear?: boolean;
    attachmentLocations: Core.AttachmentBundlerLocationEnum[];
    attachmentTypes: Core.AttachmentTypes[];
    propertyTypes: Core.PropertyTypes[];
}

export class GetPropCharValuesRequest extends GetEntitiesRequest {
    selectedPropCharName: string;
}

export class SiteStatus {
    name: string;
    id: number;
}
